import type { ElementMask, MaskType } from "../../types";
import type { CornerRadiiPx, NormalizedCurve } from "./shapePaths";
import { roundedRectWithCurvePath2D } from "./shapePaths";

function clampNumber(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function clampInt(v: unknown, min: number, max: number, fallback: number): number {
  const n = Math.round(clampNumber(v, fallback));
  return Math.max(min, Math.min(max, n));
}

function applyMaskTransform(
  p: Path2D,
  w: number,
  h: number,
  mask: ElementMask
): Path2D {
  const cx = w / 2;
  const cy = h / 2;
  const dx = clampNumber(mask.x, 0);
  const dy = clampNumber(mask.y, 0);
  const scale = Math.max(0.01, clampNumber(mask.scale, 1));
  const rotation = clampNumber(mask.rotation, 0);

  const m = new DOMMatrix();
  m.translateSelf(cx + dx, cy + dy);
  m.rotateSelf(rotation);
  m.scaleSelf(scale, scale);
  m.translateSelf(-cx, -cy);

  const out = new Path2D();
  out.addPath(p, m);
  return out;
}

function rectPath(w: number, h: number): Path2D {
  const p = new Path2D();
  p.rect(0, 0, w, h);
  return p;
}

function ellipsePath(w: number, h: number, mode: "circle" | "oval"): Path2D {
  const p = new Path2D();
  const cx = w / 2;
  const cy = h / 2;
  const rx = mode === "circle" ? Math.min(w, h) / 2 : w / 2;
  const ry = mode === "circle" ? Math.min(w, h) / 2 : h / 2;
  p.ellipse(cx, cy, Math.max(0, rx), Math.max(0, ry), 0, 0, Math.PI * 2);
  p.closePath();
  return p;
}

function regularPolygonPath(w: number, h: number, sides: number): Path2D {
  const p = new Path2D();
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.max(0, Math.min(w, h) / 2);
  const n = Math.max(3, sides);
  for (let i = 0; i < n; i++) {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) p.moveTo(x, y);
    else p.lineTo(x, y);
  }
  p.closePath();
  return p;
}

function customPath(w: number, h: number, svgPath: string): Path2D {
  const src = String(svgPath || "").trim();
  if (!src) return rectPath(w, h);
  try {
    const raw = new Path2D(src);
    // Interpret custom paths as being authored in a 0..1 box by default.
    const m = new DOMMatrix();
    m.scaleSelf(w, h);
    const out = new Path2D();
    out.addPath(raw, m);
    return out;
  } catch {
    return rectPath(w, h);
  }
}

export function buildMaskPath2D(params: {
  w: number;
  h: number;
  mask: ElementMask;
  radiiPx: CornerRadiiPx;
  curve: NormalizedCurve | null;
}): { path: Path2D; clipRule?: CanvasFillRule } {
  const { w, h, mask, radiiPx, curve } = params;
  const type: MaskType = (mask?.type as MaskType) || "rect";

  const base: Path2D = (() => {
    switch (type) {
      case "rect":
        return curve ? roundedRectWithCurvePath2D(w, h, [0, 0, 0, 0], curve) : rectPath(w, h);
      case "rounded-rect":
        return roundedRectWithCurvePath2D(w, h, radiiPx, curve);
      case "circle":
        return ellipsePath(w, h, "circle");
      case "oval":
        return ellipsePath(w, h, "oval");
      case "polygon":
        return regularPolygonPath(w, h, clampInt(mask.polygonSides, 3, 12, 6));
      case "custom":
        return customPath(w, h, String(mask.customPath || ""));
      default:
        return rectPath(w, h);
    }
  })();

  const transformed = applyMaskTransform(base, w, h, mask);

  if (mask.invert) {
    const p = new Path2D();
    p.rect(0, 0, w, h);
    p.addPath(transformed);
    return { path: p, clipRule: "evenodd" };
  }

  return { path: transformed };
}
